import { HttpService } from '@/api/shared/http.service'
import { ApiPagedPayload, ApiPagedResponse } from '@/api/shared/types'
import {
  migrateFromV1ToV2,
  migrateFromV1ToV3,
  migrateFromV1ToV4,
  transformJsonToForm
} from '@/api/shared/utils'

import type {
  AddRequestCommentDTO,
  AddRequestCommentResponse,
  RequestCommentDomain
} from './request-comments.types'
import { RequestMapper } from './requests.mapper'
import {
  Category,
  type CreateRequestApi,
  type CreateServicePassDTO,
  type CreateServiceRequestDTO,
  type RateParametersResponse,
  type RequestByIdApiType,
  type RequestCounts,
  type RequestFeedback,
  type RequestQuickFiltersResponse,
  type RequestResponse,
  type RequestsListPayload,
  type RequestStatusResponse,
  RequestTypesResponse
} from './requests.types'

class RequestsService extends HttpService {
  async getRequests(params: ApiPagedPayload<RequestsListPayload>) {
    const { data } = await this.axios.get<ApiPagedResponse<RequestResponse[]>>(
      migrateFromV1ToV2(this.getPath('/')),
      { params }
    )

    return {
      ...data,
      data: data.data.map(item => RequestMapper.toEntity(item))
    }
  }

  async getRequestById({ id }: { id: string | null }) {
    return this.axios.get<RequestByIdApiType>(
      migrateFromV1ToV3(this.getPath(`/${id}`))
    )
  }

  async getRequestCommentsList({
    id,
    ...params
  }: ApiPagedPayload<{ id: string }>) {
    return this.axios
      .get<ApiPagedResponse<RequestCommentDomain[]>>(
        this.getPath(`/${id}/comments`),
        { params }
      )
      .then(data => data.data)
  }

  async addRequestComment({ id, message, file }: AddRequestCommentDTO) {
    return this.axios.post<AddRequestCommentResponse>(
      this.getPath(`/${id}/comments`),
      transformJsonToForm({
        message,
        file
      })
    )
  }

  async createServicePass(dto: CreateServicePassDTO) {
    return this.axios.post<CreateRequestApi>(
      migrateFromV1ToV4(this.getPath('/pass')),
      transformJsonToForm(dto)
    )
  }

  async createServiceRequest(dto: CreateServiceRequestDTO) {
    return this.axios.post<CreateRequestApi>(
      migrateFromV1ToV3(this.getPath('/service')),
      transformJsonToForm(dto)
    )
  }

  async getRequestTypes(projectId: string) {
    const { data } = await this.axios.get<RequestTypesResponse>(
      migrateFromV1ToV2(this.getPath('/types')),
      {
        params: {
          projectId,
          onlyActive: true
        }
      }
    )
    return data
  }

  async getRequestCounts(projectId: string) {
    const { data } = await this.axios.get<RequestCounts>(
      this.getPath('/counts'),
      {
        params: { projectId }
      }
    )
    return data
  }

  async getRequestStatuses(projectId: string, isActive: boolean) {
    const { data } = await this.axios.get<RequestStatusResponse>(
      migrateFromV1ToV2(this.getPath('/statuses')),
      {
        params: { projectId, isActive }
      }
    )

    return data
  }

  checkIsImageFile(fileLink: string) {
    return this.axios.head(fileLink, {
      baseURL: ''
    })
  }

  getRequestFileBlob(fileLink: string) {
    return this.axios.get<Blob>(fileLink, {
      baseURL: '',
      responseType: 'blob'
    })
  }

  async getRequestQuickFilters(projectId: string) {
    const { data } = await this.axios.get<RequestQuickFiltersResponse>(
      migrateFromV1ToV2(this.getPath('/quickFilters')),
      {
        params: { projectId }
      }
    )

    return data
  }

  async getRateParameters() {
    const { data } = await this.axios.get<RateParametersResponse>(
      this.getPath('rateParameters')
    )

    return data
  }

  async addRequestFeedback(id: string, body: RequestFeedback) {
    await this.axios.post(
      migrateFromV1ToV2(this.getPath(`${id}/feedback`)),
      body
    )
  }

  async editAuto(
    autoId: string,
    body: { number: string; model: string; isStandard: boolean }
  ) {
    await this.axios.put(this.getPath(`autos/${autoId}`), body)
  }

  async getCategories(projectId: string) {
    const { data } = await this.axios.get<{ data: Category[] }>(
      this.getPath('categories'),
      {
        params: { projectId }
      }
    )

    return data
  }

  async deleteRequest(requestId: string) {
    await this.axios.delete(this.getPath(`/${requestId}`))
  }

  async reopenRequest(requestId: string) {
    await this.axios.put(this.getPath(`/${requestId}/reopen`))
  }
}

const RequestsServiceSingleton = new RequestsService('/api/v1/requests/')
export { RequestsServiceSingleton as RequestsService }
